import { FormInput, FormLabel, Form, Button, Text } from '$components'
import z from 'zod'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { invitationVerifyMutation, invitationSendVerificationCodeMutation, invitationShowQueryKey } from '$api-client/@tanstack/react-query.gen'
import { InvitationRoutePath } from '../routes/public'
import { Router } from '$utils/router'
import { useInvitation } from '../components/invitation-provider'
import { InvitationFooter } from '../components/invitation-footer'
import { QueryErrorHelper } from '$utils/query'
import { ToastHelper } from '$components'

export enum Input {
  Code = 'code',
}

const schema = z.object({
  code: z.string().min(6, { message: 'The verification code must be 6 characters long.' }),
})

export type AcceptInvitationFormValues = z.infer<typeof schema>

export function InvitationCodePage() {
  const { invitation, token } = useInvitation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutate: verifyCode, isPending: isVerifyCodeLoading, error } = useMutation({
    ...invitationVerifyMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: invitationShowQueryKey({
          path: { token },
        }),
      })

      navigate(Router.getRoute(InvitationRoutePath.InvitationsAccept, { token }))
    },
  })

  const { mutate: sendVerificationCode, isPending: isResendingVerificationCode } = useMutation({
    ...invitationSendVerificationCodeMutation(),
    onSuccess: () => ToastHelper.success('Verification code re-sent successfully. Please check your email.'),
    onError: () => ToastHelper.error('Failed to re-send the verification code. Please try again later.'),
  })

  const onSubmit = async (values: AcceptInvitationFormValues) => {
    verifyCode({
      body: {
        email: invitation.email,
        code: values.code,
      },
    })
  }

  const handleResendCode = () => {
    sendVerificationCode({
      body: {
        email: invitation.email,
      },
    })
  }

  return (
    <div className="w-full h-screen flex items-center justify-center">
      <div className="flex flex-col w-full max-w-[420px]">
        <Text type="h1">
          Enter the verification code
        </Text>

        <Text
          type="p"
          className="mb-8 mt-2"
        >
          A 6-digit verification code has been sent to your email address. Please enter the code below to verify your email.
        </Text>

        <Form
          onSubmit={onSubmit}
          schema={schema}
          errors={QueryErrorHelper.getQueryValidationErrors(error)}
        >
          {() => (
            <div className="flex flex-col gap-4">

              <div className="flex flex-col gap-2">
                <FormLabel htmlFor={Input.Code}>Code</FormLabel>
                <FormInput name={Input.Code} />
              </div>

              <Button
                type="submit"
                size="lg"
                className="mt-4"
                isLoading={isVerifyCodeLoading}
                disabled={isVerifyCodeLoading}
              >
                Verify code
              </Button>

              <div className="flex items-center">
                <Text type="s">
                  Didn&apos;t get a code?
                </Text>

                <Button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResendingVerificationCode}
                  variant="text"
                >
                  Click to resend
                </Button>
              </div>
            </div>
          )}
        </Form>

        <InvitationFooter />
      </div>
    </div>
  )
}
